if (!customElements.get('cf-curated-reviews')) {
  customElements.define(
    'cf-curated-reviews',
    class CfCuratedReviews extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.abortController = new AbortController();
        this.sectionRoot = this.closest('[data-cf-reviews]') || this;
        this.viewport = this.querySelector('[data-review-viewport]');
        this.track = this.querySelector('[data-review-track]');
        this.cards = [...this.querySelectorAll('[data-review-card]')];
        this.dots = this.querySelector('[data-review-dots]');
        this.controls = this.querySelector('[data-review-controls]');
        this.arrows = this.sectionRoot.querySelector('[data-review-arrows]');
        this.previousButton = this.sectionRoot.querySelector('[data-review-prev]');
        this.nextButton = this.sectionRoot.querySelector('[data-review-next]');
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.isRtl = getComputedStyle(this).direction === 'rtl';

        this.setupReviewExpansion();

        const signal = this.abortController.signal;
        window.addEventListener('resize', () => this.queueLayoutUpdate(), { passive: true, signal });

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.queueLayoutUpdate());
          this.resizeObserver.observe(this);
        }

        document.fonts?.ready.then(() => {
          if (this.isConnected) this.queueLayoutUpdate();
        });

        if (this.dataset.layout !== 'carousel' || !this.viewport || this.cards.length < 2) {
          this.setNavigationVisibility(false);
          return;
        }

        this.previousButton?.addEventListener('click', () => this.goToAdjacentPage(-1), { signal });
        this.nextButton?.addEventListener('click', () => this.goToAdjacentPage(1), { signal });
        this.viewport.addEventListener('scroll', () => this.queueControlUpdate(), { passive: true, signal });

        this.rebuildPagination();
        this.updateControls();
      }

      setupReviewExpansion() {
        const signal = this.abortController.signal;
        this.reviewEntries = [];

        this.querySelectorAll('[data-read-toggle]').forEach((button) => {
          const body = button.previousElementSibling;
          if (!body?.matches('[data-review-body]')) return;
          this.reviewEntries.push({ body, button });
          button.addEventListener(
            'click',
            () => {
              const expanded = body.classList.toggle('is-expanded');
              button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
              button.textContent = expanded
                ? this.dataset.showLess || 'Show less'
                : this.dataset.readMore || 'Read full review';
            },
            { signal }
          );
        });

        this.updateReviewExpansionButtons();
      }

      updateReviewExpansionButtons() {
        (this.reviewEntries || []).forEach(({ body, button }) => {
          if (body.classList.contains('is-expanded')) {
            button.hidden = false;
            return;
          }

          button.hidden = body.scrollHeight <= body.clientHeight + 2;
        });
      }

      getPagePositions() {
        if (!this.viewport) return [0];

        const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
        if (maximum <= 2) return [0];

        const count = Math.max(2, Math.ceil(this.viewport.scrollWidth / this.viewport.clientWidth));
        const positions = [];

        for (let index = 0; index < count; index += 1) {
          const position = Math.min(index * this.viewport.clientWidth, maximum);
          if (positions[positions.length - 1] !== position) positions.push(position);
        }

        return positions;
      }

      rebuildPagination() {
        this.pagePositions = this.getPagePositions();
        const hasMultiplePages = this.pagePositions.length > 1;

        this.setNavigationVisibility(hasMultiplePages);
        if (!this.dots) return;

        this.dots.replaceChildren();
        this.activePage = null;
        this.pagePositions.forEach((position, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'cf-reviews__dot';
          dot.setAttribute('aria-label', `Go to review page ${index + 1}`);
          dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
          dot.addEventListener(
            'click',
            () => this.scrollToPosition(position),
            { signal: this.abortController.signal }
          );
          this.dots.append(dot);
        });
      }

      setNavigationVisibility(visible) {
        if (this.controls) this.controls.hidden = !visible;
        if (this.arrows) this.arrows.hidden = !visible;
      }

      get scrollBehavior() {
        return this.prefersReducedMotion ? 'auto' : 'smooth';
      }

      getActivePage() {
        const current = this.getScrollPosition();
        let activePage = 0;
        let smallestDistance = Number.POSITIVE_INFINITY;

        (this.pagePositions || [0]).forEach((position, index) => {
          const distance = Math.abs(current - position);
          if (distance < smallestDistance) {
            activePage = index;
            smallestDistance = distance;
          }
        });

        return activePage;
      }

      goToAdjacentPage(direction) {
        const activePage = this.getActivePage();
        const targetPage = Math.max(0, Math.min(activePage + direction, this.pagePositions.length - 1));
        this.scrollToPosition(this.pagePositions[targetPage]);
      }

      getScrollPosition() {
        const position = this.viewport?.scrollLeft || 0;
        return this.isRtl ? Math.abs(position) : position;
      }

      scrollToPosition(position) {
        this.viewport.scrollTo({ left: this.isRtl ? -position : position, behavior: this.scrollBehavior });
      }

      updateControls() {
        if (!this.viewport) return;

        const activePage = this.getActivePage();
        const lastPage = Math.max(0, (this.pagePositions || [0]).length - 1);

        if (this.previousButton) this.previousButton.disabled = activePage === 0;
        if (this.nextButton) this.nextButton.disabled = activePage === lastPage;

        let activeDot;
        this.dots?.querySelectorAll('button').forEach((dot, index) => {
          dot.setAttribute('aria-current', index === activePage ? 'true' : 'false');
          if (index === activePage) activeDot = dot;
        });

        if (this.activePage !== activePage) {
          activeDot?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
          this.activePage = activePage;
        }
      }

      queueControlUpdate() {
        if (this.controlFrame) return;
        this.controlFrame = requestAnimationFrame(() => {
          this.controlFrame = null;
          this.updateControls();
        });
      }

      queueLayoutUpdate() {
        if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = requestAnimationFrame(() => {
          this.layoutFrame = null;
          this.updateReviewExpansionButtons();
          if (this.dataset.layout === 'carousel' && this.viewport && this.cards.length > 1) {
            this.rebuildPagination();
            this.updateControls();
          }
        });
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
        if (this.controlFrame) cancelAnimationFrame(this.controlFrame);
        if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame);
        this.initialized = false;
      }
    }
  );
}
